/**
 * The **Insights** half of S3's Analytics & Insights panel.
 *
 * > *"A selected dataset shows its insights below the navigation. What the insights are
 * > depends on the dataset type: key statistics, value / class distributions, country
 * > comparisons, temporal trends, hotspot identification."*
 *
 * Five blocks, one request: `GET /analysis/insights` (`apps/analysis/insights.py`) resolves
 * an extent, runs the §H zonal engine over it and shapes the answer by dataset type. **This
 * component computes nothing** — a second implementation of "the mean of a raster over a
 * commune" is a second set of numbers that can disagree with the map next to it.
 *
 * ## Which blocks render
 *
 * `type` is the switch, and it is the server's:
 *
 * * **categorical** (a land-cover class raster) — the class composition is both the
 *   distribution *and* the headline, so `key_stats` is null and there is nothing to print
 *   in its place; the country comparison is the modal class's share, and the trend plots
 *   that share per epoch.
 * * **continuous** (soil carbon, rainfall, ET) — key statistics with the spread under them,
 *   the country comparison as means, and the trend over published versions where the
 *   dataset has no epochs.
 *
 * A block the answer does not carry is **absent, not empty**: a dataset with one epoch has
 * no trend, and a row of axes with no line in it reads as a chart that failed to load.
 * `omitted` is the one exception — the server says *why* it skipped the trend, and a reason
 * is worth printing where a blank is not.
 *
 * ## The two-way link
 *
 * S3: *"the panel and the map are two-way linked: a selection made on the map updates the
 * panel"*. Both directions are one field each and neither needed new machinery:
 *
 * * **panel → map** — the pane above changes the drawn dataset, and this reads it.
 * * **map → panel** — the §F5.8 cascade's `scope` and the §F5.3 click's `selectedUnit` are
 *   the extent. A click on a country or a watershed therefore rescopes the numbers, and the
 *   §F5.10 crumb, the choropleth and this panel are all describing the same unit. The scope
 *   wins where both are set: a filter is a standing instruction and a click is a glance.
 *
 * Nothing here touches the map-click popup, which S3 leaves alone.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { BarsH, Spark } from '../../components/charts';
import { fetchLayers, queryKeys, type Layer } from '../../lib/api';
import {
  fetchInsights,
  INSIGHTS_TOP,
  type Insights,
  type InsightsClass,
} from '../../lib/analysis';
import { EMDASH, fmtCompact, fmtNum } from '../../lib/format';
import { selectActiveRaster, useAppStore } from '../../lib/state';
import { analysisLayerFor, seriesFor } from '../map/epochs';
import { IndicatorAnalytics } from './IndicatorAnalytics';

/** `#sidebar` is ~20% of the viewport; its own padding leaves this for a chart. */
const CHART_WIDTH = 250;

/** How many classes the distribution lists before it stops. */
const MAX_CLASSES = 6;

/** The bar colour for a block with no per-row palette of its own. */
const BAR_COLOR = '#2E7D32';

/**
 * How far a country-comparison bar fades when the filter is on some *other* country.
 *
 * The block deliberately keeps all four countries under a filter (see `focusIso`), which
 * left it looking identical filtered and unfiltered — the one block a reader could fairly
 * read as "the filter did nothing". Fading the three that are context, rather than dropping
 * them, keeps the comparison a comparison and still answers "which of these is mine".
 * Opacity on the fill, so it works for the categorical palette too, where each bar carries
 * its own modal-class colour and a substitute grey would lose the class.
 */
const CONTEXT_BAR_OPACITY = 0.32;

/** `#RRGGBB` → an `rgba()` at `CONTEXT_BAR_OPACITY`; anything else is returned unchanged. */
function dim(color: string): string {
  const hex = /^#([0-9a-f]{6})$/i.exec(color.trim())?.[1];
  if (!hex) return color;
  const n = parseInt(hex, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${CONTEXT_BAR_OPACITY})`;
}

/**
 * The insights for a **baseline variable** — the panel's other selected-dataset case.
 *
 * A theme's default is a §D5 raster or a §C survey estimate (`themeDefaultDataset`), and
 * since the 8 Sep directive ten rail chips select nothing else. `/analysis/insights` cannot
 * answer for one: it resolves a **COG** and runs the zonal engine, and a `mb_*` row has no
 * pixels anywhere — it is 1 094 stored numbers at programme, country and watershed scope.
 *
 * **So the smallest honest change is to ask the machinery that already answers for a stored
 * value**, rather than to teach the raster endpoint about a table it does not read. That
 * machinery is `IndicatorAnalytics` (§F5.9's content, R7's section), and it already shapes
 * itself for `aggregation: 'none'`:
 *
 * * **key statistics** — the big value for the extent, with the ▲/▼ against the previous
 *   reported year (absent for a 2025-only baseline, which is the honest state, not a gap);
 * * **sample n** — on the scope line, from `indicator_values.sample_n`, because it qualifies
 *   what the number is *of*;
 * * **country comparison** — the four-country bars, the same block the raster panel draws;
 * * **the sheet** — `SiblingBars`, where this estimate sits among the others off its own
 *   workbook sheet, which is the comparison a baseline reader actually wants;
 * * **no temporal trend beyond 2025** — the spark renders only where the series has a point
 *   to plot, and the baseline reports one year, so nothing draws a line through it;
 * * **"no value at this scope"** — a survey estimated at country and watershed has nothing
 *   at region level and always will, and it says so rather than printing a bare em-dash.
 *
 * Hotspots and the class distribution are absent, and that is a property of the data: a
 * ranking of communes needs values at commune level, which the survey does not have.
 *
 * `data-instype="variable"` mirrors the raster body's `categorical`/`continuous`, so one
 * assertion asks "what kind of dataset is this panel about?" whichever it is.
 */
function VariableInsights({ id, visible }: { id: string; visible: boolean }) {
  return (
    <div className="ins-body" data-insights={id} data-instype="variable">
      <IndicatorAnalytics
        indicatorId={id}
        width={CHART_WIDTH}
        showMapButton={false}
        visible={visible}
      />
    </div>
  );
}

export function InsightsPanel() {
  const { t } = useTranslation();
  const role = useAppStore((s) => s.role);
  const layerId = useAppStore(selectActiveRaster);
  /**
   * The mapped baseline variable, where one is the selected dataset.
   *
   * The raster wins if both are somehow set: `applyThemeDefault` and `pickVariable` each
   * clear the other, so the pair is transient, and the panel must describe what is on top.
   */
  const variableId = useAppStore((s) => s.indicator);
  const scope = useAppStore((s) => s.scope);
  const selectedUnit = useAppStore((s) => s.selectedUnit);
  const country = useAppStore((s) => s.country);
  const reportingLevel = useAppStore((s) => s.reportingLevel);
  const sidebarOpen = useAppStore((s) => s.sidebarOpen);

  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const layer: Layer | null = useMemo(
    () => (layers ?? []).find((l) => l.id === layerId) ?? null,
    [layers, layerId],
  );
  /** The reader's dataset — the series row, where a year of one is drawn. */
  const dataset = useMemo(
    () => seriesFor(layers, layerId) ?? layer,
    [layers, layerId, layer],
  );
  /** The row to *ask* about — the year on the slider. See `analysisLayerFor`. */
  const askId = useMemo(() => analysisLayerFor(layers, layerId), [layers, layerId]);

  // The scope wins over the click: a filter is a standing instruction, a click is a glance.
  const unit = scope?.unitId ?? selectedUnit ?? null;

  /**
   * Which of the four countries the reader has filtered to, if any — the country
   * comparison's one concession to the filter.
   *
   * That block is the only one on the panel that deliberately ignores the extent: it exists
   * to put the reader's unit *in programme context*, and narrowing a four-way comparison to
   * the one country they just chose would compare nothing (`insights.by_country` makes the
   * same argument server-side). The cost of that choice is that the block looked byte-for-
   * byte identical filtered and unfiltered, which reads as a bug rather than as a decision.
   * So the four stay and the reader's own country is the one that is not faded.
   *
   * `scope.country` before the §F1 rung: a cascade pick already carries its country, and a
   * scope always agrees with `country` anyway (`setCountry` drops a scope from elsewhere).
   */
  const focusIso = scope?.country ?? (country === 'ALL' ? null : country);
  /**
   * Hotspots are ranked at a level, and the level the map is reporting at is the one the
   * reader is looking at — except at country level, where "the top 4 of 4" is not a ranking.
   * The server's own default (regions) is the fallback, and an overlay level goes through
   * verbatim because `zonal.zones` takes the type there.
   */
  const level = reportingLevel === 0 ? 1 : reportingLevel;

  /**
   * A **display-only** layer has no raster of ours behind it (§1a rule 4) — NASA's GIBS
   * NDVI is drawn through the proxy and never stored — so there is nothing for the zonal
   * engine to read and the request would come back 422. The panel says so instead of
   * asking, which is also what the Ecosystem theme's own note says one line above.
   */
  const analysable = Boolean(
    layerId && layer && !layer.display_only && layer.has_data !== false,
  );

  const insights = useQuery({
    queryKey: queryKeys.insights(askId ?? '', unit, country, level, INSIGHTS_TOP),
    queryFn: () =>
      fetchInsights({ layer: askId as string, unit, country, level, top: INSIGHTS_TOP }),
    // A collapsed panel is 400 px off-screen; the zonal sweep behind this is the most
    // expensive read in the explorer and must not be paid for a panel nobody can see.
    enabled: Boolean(askId) && analysable && sidebarOpen,
    // The numbers behind a published dataset do not move while a reader is clicking.
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  return (
    <section id="sbInsights" aria-label={t('insights.title')}>
      <div className="sb-title sba-head">
        <span className="sba-title-text">{t('insights.title')}</span>
      </div>

      {!layerId && variableId ? (
        <VariableInsights id={variableId} visible={sidebarOpen} />
      ) : !layerId ? (
        <div className="sba-empty" data-insightsempty="">
          {t('insights.noDataset')}
        </div>
      ) : !analysable ? (
        <div className="sba-empty" data-insightsempty="display-only">
          {t('insights.notAnalysable', { name: dataset?.name ?? layerId })}
        </div>
      ) : insights.isPending ? (
        <div className="sba-empty" data-insightsloading="">
          {t('insights.loading')}
        </div>
      ) : insights.isError ? (
        <div className="note warn" data-insightserror="">
          {t('insights.failed')}
        </div>
      ) : (
        <InsightsBody data={insights.data} focusIso={focusIso} />
      )}
    </section>
  );
}

function InsightsBody(
  { data, focusIso }: { data: Insights; focusIso: string | null },
) {
  const { t } = useTranslation();
  const unit = data.layer.unit ?? undefined;
  const categorical = data.type === 'categorical';

  return (
    <div className="ins-body" data-insights={data.layer.id} data-instype={data.type}>
      <div className="ins-extent" data-insextent={data.extent.kind}>
        {data.extent.label ?? t('insights.extentProgramme')}
      </div>

      {/* 1 — key statistics. Continuous only: the mean of a class code is the one number a
          categorical dataset must never show, which is why the server sends null here. */}
      {data.key_stats ? (
        <div className="ins-block" data-insblock="key_stats">
          <div className="ins-head">{t('insights.keyStats')}</div>
          <div className="ins-stat-row">
            <Stat label={t('insights.mean')} value={data.key_stats.mean} unit={unit} />
            <Stat label={t('insights.min')} value={data.key_stats.min} unit={unit} />
            <Stat label={t('insights.max')} value={data.key_stats.max} unit={unit} />
          </div>
          {/* An aggregated extent has no median and no standard deviation to report — see
              `AGGREGATABLE_STATS`. Saying so is the block; printing a blank pair is not. */}
          {data.key_stats.aggregated ? (
            <div className="ins-note" data-insaggregated="">
              {t('insights.aggregated', { n: data.n_zones })}
            </div>
          ) : (
            <div className="ins-stat-row">
              <Stat label={t('insights.median')} value={data.key_stats.median} unit={unit} />
              <Stat label={t('insights.std')} value={data.key_stats.std} unit={unit} />
            </div>
          )}
        </div>
      ) : null}

      {/* 2 — the class distribution. */}
      {data.distribution?.length ? (
        <div className="ins-block" data-insblock="distribution">
          <div className="ins-head">{t('insights.distribution')}</div>
          <ClassBars rows={data.distribution.slice(0, MAX_CLASSES)} />
        </div>
      ) : null}

      {/* 3 — the country comparison. Always the four, whatever the extent — see
          `focusIso`, which is how the block shows the filter without shrinking to it. */}
      {data.countries.length ? (
        <div className="ins-block" data-insblock="countries" data-insfocus={focusIso ?? ''}>
          <div className="ins-head">
            {categorical ? t('insights.countriesClass') : t('insights.countries')}
          </div>
          <BarsH
            labels={data.countries.map((c) => c.label)}
            series={[
              {
                key: 'countries',
                label: categorical ? t('insights.share') : (unit ?? t('insights.mean')),
                color: BAR_COLOR,
                values: data.countries.map((c) => c.value),
                rowColors: data.countries.map((c) => {
                  const base = categorical ? (c.top_color ?? BAR_COLOR) : BAR_COLOR;
                  return !focusIso || c.country === focusIso ? base : dim(base);
                }),
              },
            ]}
            w={CHART_WIDTH}
            rowH={20}
            labelGutter={64}
            labelSize={10}
            unit={categorical ? '%' : (unit ?? undefined)}
            fmt={1}
            ariaLabel={t('insights.countriesAria', { name: data.layer.name })}
          />
          {focusIso ? (
            <div className="ins-note" data-inscountriesnote="context">
              {t('insights.countriesContext')}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* 4 — the temporal trend. */}
      {data.trend?.omitted ? (
        <div className="ins-block" data-insblock="trend">
          <div className="ins-head">{t('insights.trend')}</div>
          <div className="ins-note" data-instrendomitted={data.trend.omitted}>
            {t('insights.trendOmitted')}
          </div>
        </div>
      ) : data.trend && data.trend.points.length > 1 ? (
        <div className="ins-block" data-insblock="trend">
          <div className="ins-head">
            {t(data.trend.kind === 'epoch' ? 'insights.trendEpoch' : 'insights.trendVersion')}
          </div>
          <Spark
            series={data.trend.points.map((p) => p.value)}
            labels={data.trend.points.map((p) => p.year ?? p.period ?? '')}
            w={CHART_WIDTH}
            h={46}
            activeIdx={data.trend.points.length - 1}
            unit={categorical ? '%' : (unit ?? undefined)}
            fmt={1}
            ariaLabel={t('insights.trendAria', { name: data.layer.name })}
          />
        </div>
      ) : null}

      {/* 5 — hotspots. A ranking, not a threshold: "hotspot" here is "where is this
          highest", and a cut-off would need a per-dataset value nobody has agreed. */}
      {data.hotspots.rows.length ? (
        <div className="ins-block" data-insblock="hotspots">
          <div className="ins-head">
            {t('insights.hotspots', { n: data.hotspots.rows.length })}
          </div>
          <ol className="ins-hotspots">
            {data.hotspots.rows.map((row) => (
              <li key={row.unit_id} data-inshotspot={row.unit_id}>
                <span className="ins-hs-name" title={row.class_label ?? undefined}>
                  {row.name}
                </span>
                <span className="ins-hs-val">
                  {fmtNum(row.value, 1)}
                  {data.hotspots.stat === 'top_class_share' ? '%' : ''}
                </span>
              </li>
            ))}
          </ol>
          <div className="ins-note">
            {t('insights.hotspotsNote', { n: data.hotspots.n_units })}
          </div>
        </div>
      ) : null}

      {/* §E7's provenance line: what was read, at what resolution. A panel that prints a
          number must be able to say where it came from. */}
      <div className="ins-source" data-inssource="">
        {t('insights.source', {
          name: data.layer.name,
          version: data.layer.version,
          m: fmtNum(data.resolution_m, 0),
        })}
      </div>
    </div>
  );
}

function Stat({
  label, value, unit,
}: { label: string; value: number | null; unit?: string }) {
  return (
    <div className="ins-stat" data-insstat={label}>
      <div className="ins-stat-lab">{label}</div>
      <div className="ins-stat-val">
        {value === null || value === undefined ? EMDASH : fmtCompact(value)}
        {value !== null && value !== undefined && unit ? <small> {unit}</small> : null}
      </div>
    </div>
  );
}

/**
 * The class composition, as its own bars rather than through `BarsH`.
 *
 * Every class already has a colour — the catalogue's, the one the map is painting it in —
 * so the chart is a swatch, a name and a share, and drawing it as a bar chart would put a
 * shared axis under six rows whose only comparison is to 100%. The legend and the map are
 * reading one class table (`serializers.class_table`).
 */
function ClassBars({ rows }: { rows: InsightsClass[] }) {
  return (
    <div className="ins-classes">
      {rows.map((row) => (
        <div className="ins-class" key={row.value} data-insclass={row.value}>
          <span
            className="ins-class-sw"
            aria-hidden="true"
            style={{ background: row.color ?? '#8B958A' }}
          />
          <span className="ins-class-name" title={row.label}>
            {row.label}
          </span>
          <span className="ins-class-bar" aria-hidden="true">
            <i style={{ width: `${Math.max(1, Math.round(row.pct ?? 0))}%`,
                        background: row.color ?? '#8B958A' }} />
          </span>
          <span className="ins-class-pct">{fmtNum(row.pct, 1)}%</span>
        </div>
      ))}
    </div>
  );
}

export default InsightsPanel;
